// Laufzeit-Kontrastwächter für den Backend-Bereich (.bw): sorgt dafür, dass KEIN Button/Link
// mit zu geringem Kontrast gerendert wird – auch wenn er dynamisch (Dialog, State-Wechsel,
// Seitenwechsel) ins DOM kommt. Es wird ausschliesslich die Schriftfarbe korrigiert.
const MIN_RATIO = 4.5;
const SEL = 'button, [role="button"], a[class*="bg-"], .btn, [data-slot="button"], '
  + '[data-state="on"], [aria-pressed="true"], [data-selected="true"], '
  + 'label[class*="bg-"], [class*="bg-["], [style*="background"]';
const STYLE_ID = "yg-contrast-guard-style";

const toRgb = (str) => {
  const m = String(str || "").match(/-?[\d.]+/g);
  if (!m) return null;
  return { r: +m[0], g: +m[1], b: +m[2], a: m[3] === undefined ? 1 : +m[3] };
};

const relLum = ({ r, g, b }) => {
  const f = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

const blend = (fg, bg) => ({
  r: fg.r * fg.a + bg.r * (1 - fg.a),
  g: fg.g * fg.a + bg.g * (1 - fg.a),
  b: fg.b * fg.a + bg.b * (1 - fg.a),
  a: 1,
});

const ratio = (a, b) => {
  const l1 = relLum(a), l2 = relLum(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

function effectiveBg(el) {
  const stack = [];
  let node = el;
  while (node && node.nodeType === 1) {
    const bg = toRgb(getComputedStyle(node).backgroundColor);
    if (bg && bg.a > 0.001) {
      stack.push(bg);
      if (bg.a >= 0.999) break;
    }
    node = node.parentElement;
  }
  const dark = document.documentElement.classList.contains("bw-dark");
  let base = dark ? { r: 10, g: 10, b: 10, a: 1 } : { r: 255, g: 255, b: 255, a: 1 };
  for (let i = stack.length - 1; i >= 0; i--) base = blend(stack[i], base);
  return base;
}

function fix(el) {
  if (!el || el.nodeType !== 1 || !el.isConnected) return;
  if (el.hasAttribute("data-yg-no-contrast-fix")) return;
  // G1: Nur im RUHEZUSTAND messen. Elemente unter dem Mauszeiger zeigen ihre Hover-Farben –
  // eine dort gesetzte Inline-Farbe bliebe danach falsch stehen.
  try {
    if (el.matches(":hover")) return;
  } catch { /* :hover nicht unterstützt → normal weiter */ }
  // N24: Läuft gerade eine Farb-/Transform-Transition, zeigt getComputedStyle einen
  // Zwischenwert. Dann später erneut messen, nicht jetzt eine falsche Farbe festschreiben.
  try {
    const running = typeof el.getAnimations === "function"
      ? el.getAnimations().some((a) => a.playState === "running")
      : false;
    const cs = running ? null : getComputedStyle(el);
    const dur = cs ? (cs.transitionDuration || "0s") : "0s";
    const mitTransition = dur.split(",").some((d) => parseFloat(d) > 0);
    if (running || (mitTransition && el.dataset.ygTransitionPending !== "done")) {
      if (running) {
        el.dataset.ygTransitionPending = "wait";
        Promise.allSettled(el.getAnimations().map((a) => a.finished)).then(() => {
          el.dataset.ygTransitionPending = "done";
          fix(el);
        });
        return;
      }
      el.dataset.ygTransitionPending = "done";
    }
  } catch { /* getAnimations nicht unterstützt → normal weiter */ }
  // Günstiger Pfad: unkorrigierte Elemente mit gutem Kontrast werden nicht angefasst.
  if (!el.dataset.ygFix) {
    const c0 = getComputedStyle(el);
    if (c0.display === "none" || c0.visibility === "hidden") return;
    const f0 = toRgb(c0.color);
    if (!f0) return;
    const b0 = effectiveBg(el);
    if (ratio(blend(f0, b0), b0) >= MIN_RATIO) return;
  } else {
    // Nach einem Theme-Wechsel muss die THEME-eigene Farbe neu gemessen werden.
    delete el.dataset.ygFix;
    el.style.removeProperty("color");
  }
  const cs = getComputedStyle(el);
  if (cs.display === "none" || cs.visibility === "hidden") return;
  // Ohne sichtbaren Text/Icon gibt es keine Schriftfarbe zu retten (z.B. Schalter).
  if (!(el.textContent || "").trim() && !el.querySelector("svg, img")) return;
  const fg = toRgb(cs.color);
  if (!fg) return;
  const bg = effectiveBg(el);
  const text = blend(fg, bg);
  if (ratio(text, bg) >= MIN_RATIO) return;
  // Die Variante mit dem BESSEREN Kontrast gewinnt (nicht bloß eine Helligkeitsschwelle) –
  // sonst bleibt z.B. weiße Schrift auf hellblauem Button zu blass.
  const dark = { r: 10, g: 10, b: 10, a: 1 };
  const light = { r: 255, g: 255, b: 255, a: 1 };
  const target = ratio(dark, bg) >= ratio(light, bg) ? "#0a0a0a" : "#ffffff";
  el.dataset.ygFix = target;
  el.style.setProperty("color", target, "important");
}

function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `[data-yg-fix] *:not(svg):not(path){color:inherit !important}
[data-yg-fix] svg{color:inherit !important}
html [data-yg-fix][data-yg-fix] *:not(svg):not(path){color:inherit !important}`;
  document.head.appendChild(s);
}

let observer = null;
let htmlObserver = null;
let sweepTimer = null;
let queue = new Set();
let scheduled = false;

const flush = () => {
  scheduled = false;
  const items = Array.from(queue);
  queue = new Set();
  // Ein einzelnes Problem-Element darf den Rest nicht blockieren.
  items.forEach((el) => {
    try { fix(el); } catch { /* ignorieren */ }
  });
};

const schedule = (els) => {
  els.forEach((e) => queue.add(e));
  if (scheduled) return;
  scheduled = true;
  setTimeout(flush, 60);
  // G1: 200-ms-Nachmessung – CSS-Transitions und Folge-Renders sind dann fertig.
  setTimeout(() => {
    const again = Array.from(els);
    again.forEach((el) => { try { fix(el); } catch { /* ignorieren */ } });
  }, 200);
};

const collect = (root) => {
  const out = [];
  if (root.nodeType !== 1) return out;
  if (root.matches?.(SEL)) out.push(root);
  root.querySelectorAll?.(SEL)?.forEach((n) => out.push(n));
  return out;
};

const scanAll = () => schedule(collect(document.body));

const clearAll = () => {
  document.querySelectorAll("[data-yg-fix]").forEach((el) => {
    delete el.dataset.ygFix;
    el.style.removeProperty("color");
  });
};

// Theme-Wechsel: eigene Korrekturen sofort entfernen (Buttons zeigen ihre Theme-Farbe)
// und danach mehrfach neu messen – Farb-Transitions brauchen ein paar hundert ms.
let themeTimers = [];
const onThemeChange = () => {
  themeTimers.forEach(clearTimeout);
  // Farb-Transitions brauchen ein paar hundert ms – mehrfach nachmessen.
  // `fix()` misst immer die Theme-Farbe, daher ist kein Zurücksetzen nötig.
  themeTimers = [30, 150, 400, 800, 1400].map((t) => setTimeout(scanAll, t));
};

const onPointerOver = null;   // G1: entfernt, es wird nur im Ruhezustand gemessen

export function startContrastGuard() {
  if (observer) return;
  ensureStyle();
  scanAll();
  observer = new MutationObserver((records) => {
    const els = [];
    for (const r of records) {
      if (r.type === "childList") {
        r.addedNodes.forEach((n) => els.push(...collect(n)));
      } else if (r.target?.nodeType === 1) {
        els.push(...collect(r.target));
      }
    }
    if (els.length) schedule(els);
  });
  observer.observe(document.body, {
    childList: true, subtree: true, attributes: true,
    attributeFilter: ["class", "disabled", "data-state"],
  });
  window.addEventListener("yg-backend-mode", onThemeChange);
  // Selbstheilung: regelmäßiger Nachlauf, damit Buttons nach Theme-Wechsel,
  // Farb-Transitions oder Re-Renders NIE ohne Kontrast dastehen (kein Hover nötig).
  sweepTimer = setInterval(() => { if (!document.hidden) scanAll(); }, 4000);
  htmlObserver = new MutationObserver(onThemeChange);
  htmlObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
}

export function stopContrastGuard() {
  clearInterval(sweepTimer);
  sweepTimer = null;
  themeTimers.forEach(clearTimeout);
  themeTimers = [];
  observer?.disconnect();
  observer = null;
  htmlObserver?.disconnect();
  htmlObserver = null;
  window.removeEventListener("yg-backend-mode", onThemeChange);
  queue = new Set();
  clearAll();
}
